import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search, Archive } from 'lucide-react';
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';

interface CarRow {
  id: string;
  stockNumber: string | null;
  vin: string | null;
  year: number | null;
  make: string | null;
  model: string | null;
  color: string | null;
  status: 'DRAFT' | 'IN_PROGRESS' | 'COMPLETE' | 'ARCHIVED';
  photoCount: number;
  thumbnailUrl: string | null;
  updatedAt: string;
}

export function CarsListPage() {
  const [q, setQ] = useState('');
  const cars = useQuery({
    queryKey: ['cars', q],
    queryFn: () => api.get<{ items: CarRow[]; total: number }>('/cars', { query: { q, pageSize: 50 } }),
  });

  return (
    <div className="container py-8 space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Cars</h1>
          <p className="text-sm text-muted-foreground">
            {cars.data ? `${cars.data.total} active` : ' '}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 sm:flex-initial">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Stock # / VIN / make" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 sm:w-64" />
          </div>
          <Button asChild className="gap-2">
            <Link to="/app/cars/new"><Plus className="h-4 w-4" /> New</Link>
          </Button>
        </div>
      </header>

      {cars.isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-48" />)}
        </div>
      )}

      {cars.data && cars.data.items.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
            <Archive className="h-8 w-8 text-muted-foreground" />
            <div className="text-lg font-medium">No cars yet.</div>
            <div className="text-sm text-muted-foreground">Start your first car to see it here.</div>
            <Button asChild className="gap-2">
              <Link to="/app/cars/new"><Plus className="h-4 w-4" /> New car</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {cars.data && cars.data.items.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cars.data.items.map((c) => (
            <Link key={c.id} to={`/app/cars/${c.id}`}>
              <Card className="overflow-hidden transition-colors hover:border-primary/50">
                <div className="relative aspect-[4/3] bg-gradient-to-br from-muted to-secondary">
                  {c.thumbnailUrl ? (
                    <img src={c.thumbnailUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-muted-foreground">No photos yet</div>
                  )}
                  <Badge variant={c.status === 'COMPLETE' ? 'success' : c.status === 'DRAFT' ? 'outline' : 'warning'} className="absolute right-2 top-2">
                    {c.status.toLowerCase().replace('_', ' ')}
                  </Badge>
                </div>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold truncate">{c.stockNumber ?? c.vin ?? 'Unnamed'}</div>
                    <div className="text-xs text-muted-foreground">{c.photoCount}/20</div>
                  </div>
                  <div className="truncate text-sm text-muted-foreground">
                    {[c.year, c.make, c.model].filter(Boolean).join(' ') || '—'}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
